import type { ReactNode } from 'react'

/**
 * The one statement that belongs beside every price (ADR-0054).
 *
 * Owner ruling, 2026-09-15: every price and every receipt says that the final
 * amount may change at the hospital and a top-up may be required — not only the
 * variable ones. The owner's example is why, and it is in the copy because it
 * explains the rule better than any abstraction could:
 *
 *   > A client going for an MRI may be asked to do a kidney function test
 *   > first, which alters the final cost of the service.
 *
 * SPECIFIC, NOT VAGUE. "Prices may vary" is a get-out clause: it protects us and
 * tells the patient nothing. Naming what can change — the hospital finding more
 * is needed — is information they can act on.
 *
 * ONE PER SCREEN, NOT ONE PER ROW. The owner called it a "general statement",
 * and it is: a sentence repeated under ten hospital prices is a sentence nobody
 * reads. The per-row distinction is carried by the price itself — a figure, a
 * "From", or the line saying AMTC will confirm it.
 */
export function PriceCaveat({
  className = '',
}: {
  className?: string
}): ReactNode {
  return (
    <p
      className={`text-[12px] leading-relaxed text-ink-3 ${className}`.trim()}
    >
      Prices cover the procedure itself. If the hospital finds you need
      something additional — a kidney function test before an MRI, for
      instance — that is charged separately and paid at the hospital.
    </p>
  )
}
